import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerField } from "@/components/DatePickerField";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

const fmtTs = (d) => (d ? new Date(d).toLocaleString() : "never");
const STATUS = {
  UPDATED: ["New carrier events applied", "text-sage"], UP_TO_DATE: ["Up to date with carrier", "text-sage"], FRESH: ["Up to date with carrier", "text-sage"],
  NO_CARRIER_EVENTS: ["Carrier has no events yet", "text-warmgrey"], AWAITING_DISPATCH_CONFIRMATION: ["Carrier history found — confirm historical dispatch", "text-amber-700"],
  REFERENCE_MISMATCH: ["Reference mismatch — review required", "text-red-700"], ERROR: ["Carrier lookup failed", "text-red-700"],
  NOT_TRACKABLE: ["Not trackable", "text-warmgrey"], SYNC_DISABLED: ["Automatic tracking disabled", "text-warmgrey"], IN_PROGRESS: ["Checking…", "text-warmgrey"],
};

export const TrackingSync = ({ container, sync, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [label, cls] = STATUS[sync?.tracking_status] || ["Never checked", "text-warmgrey"];
  const check = sync?.check;
  const pending = sync?.pending_history;

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/containers/${container.id}/tracking/sync`, { force: true });
      const s = r.data.status;
      if (s === "ERROR" || s === "REFERENCE_MISMATCH") toast.error(`Carrier check: ${Array.isArray(r.data.detail) ? r.data.detail.join("; ") : r.data.detail}`);
      else toast.success(`Carrier check: ${(STATUS[s] || [s])[0]}${r.data.applied ? ` (${r.data.applied} new)` : ""}`);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const r = await api.post(`/containers/${container.id}/tracking/confirm-historical-dispatch`, { dispatch_date: date, note });
      toast.success(`Historical dispatch confirmed — ${r.data.applied} carrier events applied, stage ${r.data.transit_stage?.replace(/_/g, " ")}`);
      setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setConfirming(false); }
  };

  return (
    <div className="mt-4 rounded-md border border-border bg-white p-4" data-testid="tracking-sync">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1 text-xs">
          <p className={`flex items-center gap-1.5 text-sm font-medium ${cls}`} data-testid="tracking-sync-status">
            {sync?.tracking_status === "ERROR" || sync?.tracking_status === "REFERENCE_MISMATCH" ? <AlertTriangle className="h-4 w-4" /> : sync?.tracking_status === "UPDATED" || sync?.tracking_status === "UP_TO_DATE" ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
            {label}
          </p>
          <p className="text-warmgrey">
            <span data-testid="tracking-last-checked">Last checked: {fmtTs(sync?.last_checked_at)}</span> · <span data-testid="tracking-last-synced">Last updated from carrier: {fmtTs(sync?.last_updated_at)}</span>
            {check?.reference && <> · via {check.reference}{check.eta_reference && check.eta_reference !== check.reference ? ` + B/L ${check.eta_reference}` : ""}</>}
            {check?.ok && <> · {check.carrier_events} mapped / {check.ignored_events} ignored{check.eta_available === false ? " · no carrier ETA (needs bill of lading)" : ""}</>}
          </p>
          {check && !check.ok && <p className="text-red-700" data-testid="tracking-sync-error">{check.error_kind}: {check.error}</p>}
          {!sync?.eligible && <p className="text-amber-700" data-testid="tracking-not-trackable">{sync?.reason}</p>}
        </div>
        <Button size="sm" variant="outline" onClick={refresh} disabled={busy || !sync?.eligible} className="h-9 border-limestone" data-testid="tracking-refresh-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="mr-1 h-3.5 w-3.5" />Refresh tracking</>}
        </Button>
      </div>

      {pending && container.status === "IN_MOROCCO" && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="pending-history">
          <p className="font-semibold">Carrier history exists but this container is still marked IN MOROCCO.</p>
          <p className="mt-1">ZIM reports {pending.events.length} verified milestones (earliest {pending.earliest_carrier_event}). Nothing has been applied. Confirm the real factory dispatch date to move Morocco stock into transit and load the carrier timeline. Florida inventory is not affected.</p>
          <ul className="mt-2 space-y-0.5">
            {pending.events.map((e, i) => <li key={i} data-testid={`pending-event-${i}`}>{e.event_type.replace(/_/g, " ")} · {e.display || e.event_time} · {e.location}{e.vessel ? ` · ${e.vessel}/${e.voyage}` : ""} <span className="uppercase text-[10px]">{e.event_kind}</span></li>)}
          </ul>
          <Button size="sm" className="mt-3 h-8 bg-charcoal text-white hover:bg-charcoal/90" onClick={() => { setDate(""); setOpen(true); }} data-testid="confirm-historical-dispatch-btn">Confirm historical dispatch…</Button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="historical-dispatch-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">Confirm historical factory dispatch</DialogTitle></DialogHeader>
          <p className="text-sm text-warmgrey">Enter the date the container actually left the factory. It must be on or before the first carrier event ({pending?.earliest_carrier_event}). This moves the container's Morocco stock into transit and applies the carrier milestones; it does not create Florida inventory.</p>
          <div className="space-y-1"><Label className="text-xs uppercase tracking-wider text-warmgrey">Factory dispatch date</Label><DatePickerField value={date} onChange={setDate} max={pending?.earliest_carrier_event?.slice(0, 10)} className="h-10" testId="historical-dispatch-date" /></div>
          <div className="space-y-1"><Label className="text-xs uppercase tracking-wider text-warmgrey">Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. per supplier packing list" data-testid="historical-dispatch-note" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={confirm} disabled={!date || confirming} className="bg-charcoal text-white hover:bg-charcoal/90" data-testid="historical-dispatch-submit">{confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm dispatch & apply carrier history"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
